import { useState, useContext, useMemo, useCallback, useRef } from 'react';
import axios from 'src/axios';
import { AppContext } from 'src/store/ContextStore';
import { EVENTS } from 'src/components/admin/appReducer';
import { toStoredDate } from 'src/util/util';
import { SuggestionFamilyContext } from 'src/components/UI/clinicalSuggestions/SuggestionFamilyContext';
import {
  matchSubject,
  memberKey,
  targetOf
} from 'src/components/UI/clinicalSuggestions/relativeMatch';
import {
  carriesPrenatalScope,
  namesScope
} from 'src/components/pedigree-chart/pedigree-sidebar/sections/clinical-notes-section/forms/prenatalScope';
import { withoutGenotypeSuggestions } from 'src/components/UI/clinicalSuggestions/prenatalSuggestions';

// THE RECORD'S PRENATAL SCOPE, as the accept sends it (TGCLOUD-1868, decision 17). A Test or a
// Clinical Note opened for a fetus is the MOTHER's record, scoped to the pregnancy (and fetus), so a
// genotype accepted from its document would land on her. The server writes no genotype from a source
// about a pregnancy; a saved Test it reads for itself (`sourceTestId`), and every other record, a Test
// not saved yet or a Clinical Note, says its scope here. Only the fields that name something are sent,
// and nothing at all for an ordinary record, so its accept is what it always was.
const SCOPE_KEYS = ['pregnancyId', 'fetusId'];
function prenatalScopeToSend(scope) {
  if (!scope || typeof scope !== 'object' || !carriesPrenatalScope(scope)) {
    return null;
  }
  const out = {};
  for (const key of SCOPE_KEYS) {
    if (namesScope(scope[key])) out[key] = scope[key];
  }
  return out;
}

// Suggested Clinical Terms for one clinical-record form (TGCLOUD-1845).
//
// The analysis answers per PERSON: `subjects` = [{ key, relation, label,
// name, findings: [{ kind, id, name, status, preselect, evidence, … }] }].
// The patient's definite findings start ticked; a relative's start ticked only
// when the pedigree has exactly one person who fits (relativeMatch.js) —
// otherwise the clinician picks who they are, or adds nothing for them.

const findingKey = (subjectKey, f) => `${subjectKey}|${f.kind}|${f.id}`;

// A pre-1845 answer (three flat lists for the patient) as subjects.
function subjectsOf(data) {
  if (Array.isArray(data?.subjects)) return data.subjects;
  const as = (kind) => (x) => ({
    ...x,
    kind,
    status: 'present',
    preselect: false,
    evidence: []
  });
  return [
    {
      key: 'self',
      relation: 'self',
      label: 'This patient',
      name: null,
      findings: [
        ...(data?.phenotypes || []).map(as('phenotype')),
        ...(data?.diseases || []).map(as('disease')),
        ...(data?.genotypes || []).map(as('genotype'))
      ]
    }
  ];
}

// Findings from several uploads and the typed note, merged per person.
//
// One identity check is kept — the last that found a mismatch — and it names
// every uploaded file whose own check found one (`sources`), so with several
// documents in play the warning can say WHICH may be about someone else
// (TGCLOUD-1850).
//
// `noFindings` names every UPLOADED file whose findings were not read
// (TGCLOUD-1866 D4): no part of it was read as findings, or a variant result
// in it sat outside every part that was (`variantTextNotRead`, the review of
// D4). Its empty result means "not read", not "nothing found", and the panel
// has to say so. A typed note is left out: the clinician wrote it, and a note
// that is all plan has nothing to go back and read.
//
// `terminology` is unavailable when ANY analysis could not be looked up, and
// then names the files that were not (`sources`). It used to be the last
// analysis's answer, and the typed note comes last, so a note analysed after
// an upload hid the upload's "too long" (the review of D10).
//
// `genotypesNotOffered` adds up what the server left out of each document that
// gives results for more than one person, or for a pregnancy (TGCLOUD-1868
// review), and names those files (`sources`).
function mergeAnalyses(list) {
  const subjects = new Map();
  const familyNotes = [];
  let identityCheck = null;
  const identitySources = [];
  let noFindings = null;
  let terminology = null;
  let notLookedUp = null;
  let engine = null;
  let genotypesNotOffered = null;
  for (const a of list) {
    if (!a) continue;
    engine = engine || a.engine || null;
    if (a.genotypesNotOffered?.count > 0) {
      genotypesNotOffered = genotypesNotOffered || {
        reason: a.genotypesNotOffered.reason,
        count: 0,
        sources: []
      };
      genotypesNotOffered.count += a.genotypesNotOffered.count;
      if (a.sourceName) genotypesNotOffered.sources.push(a.sourceName);
    }
    if (a.terminology?.available === false) {
      notLookedUp = notLookedUp || { ...a.terminology, sources: [] };
      if (a.sourceName) notLookedUp.sources.push(a.sourceName);
    } else if (a.terminology) terminology = a.terminology;
    if (
      a.sourceKind === 'upload' &&
      (a.analysis?.noFindingsSection || a.analysis?.variantTextNotRead)
    ) {
      noFindings = noFindings || { sources: [] };
      if (a.sourceName) noFindings.sources.push(a.sourceName);
    }
    if (
      a.identityCheck &&
      (a.identityCheck.dobMismatch || a.identityCheck.surnameMismatch)
    ) {
      identityCheck = a.identityCheck;
      if (a.sourceName) identitySources.push(a.sourceName);
    }
    familyNotes.push(...(a.familyNotes || []));
    for (const s of a.subjects || []) {
      if (!subjects.has(s.key)) subjects.set(s.key, { ...s, findings: [] });
      const into = subjects.get(s.key);
      for (const f of s.findings || []) {
        if (!into.findings.some((g) => g.kind === f.kind && g.id === f.id))
          into.findings.push(f);
      }
    }
  }
  return {
    subjects: [...subjects.values()],
    familyNotes,
    identityCheck: identityCheck
      ? { ...identityCheck, sources: identitySources }
      : null,
    noFindings,
    terminology: notLookedUp || terminology,
    engine,
    genotypesNotOffered
  };
}

// `sourceTestId`: the Test note this form is editing, when there is one. The
// server takes it as the source of the edited person's accepted variants once
// it has checked the Test is theirs (TGCLOUD-1866 D8).
// `sourceScope`: the prenatal scope of the record the form is editing
// (`{ pregnancyId?, fetusId? }`), new or saved. From a record about a
// pregnancy the server writes no genotype (TGCLOUD-1868, decision 17), so
// none is offered either (decision 19, prenatalSuggestions.js): the analysis
// is kept without its genotype suggestions, and says how many it left out
// (`analysis.genotypesWithheld`), for the panel to say why.
export function useClinicalSuggestions({
  familyMemberId,
  isProband,
  onAccepted,
  sourceTestId = null,
  sourceScope = null
}) {
  const { content, dispatchUpdate } = useContext(AppContext);
  const family = useContext(SuggestionFamilyContext) || {};
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const user_id = user?.user_id || user?._id;

  const [attachmentAnalyses, setAttachmentAnalyses] = useState([]);
  // Uploaded files whose findings must not be offered: a lab report sent to the lab report inbox
  // as someone else's (TGCLOUD-1866 Phase 3). A ref, so an analysis still in flight when its file
  // is forgotten is dropped when it lands.
  const forgottenSources = useRef(new Set());
  const [noteAnalysis, setNoteAnalysis] = useState(null);
  // How many analyses are in flight. Several uploads can each be analysed
  // while the typed note is too; a single boolean was cleared by whichever
  // finished first, hiding "Analysing…" while others were still running.
  const [analysesInFlight, setAnalysesInFlight] = useState(0);
  const analyzingNote = analysesInFlight > 0;
  const [savingNote, setSavingNote] = useState(false);
  // findingKey → ticked. Absent means "use the default" (see isSelected).
  const [ticks, setTicks] = useState({});
  // subjectKey → memberKey, or '' for "add nothing for this person".
  const [targets, setTargets] = useState({});
  const [lastAccept, setLastAccept] = useState(null);
  // The HPO release the latest suggestions were matched against, for the
  // footer the HPO licence asks for (TGCLOUD-1846).
  const [hpoRelease, setHpoRelease] = useState(null);

  // A record about a pregnancy is offered no genotype (decision 19): what is shown, ticked and
  // accepted is all read from this analysis, so none can reach the accept either.
  const aboutPregnancy = carriesPrenatalScope(sourceScope);
  const analysis = useMemo(() => {
    const merged = mergeAnalyses([...attachmentAnalyses, noteAnalysis]);
    return aboutPregnancy ? withoutGenotypeSuggestions(merged) : merged;
  }, [attachmentAnalyses, noteAnalysis, aboutPregnancy]);

  const members = useMemo(() => family.members || [], [family.members]);
  const selectedMember = family.selected || null;
  const matches = useMemo(() => {
    const out = {};
    for (const s of analysis.subjects) {
      if (s.key === 'self' || s.relation === 'partner') continue;
      out[s.key] = selectedMember
        ? matchSubject(
            { relation: s.relation, name: s.name },
            selectedMember,
            members
          )
        : { preMatch: null, candidates: [], others: [] };
    }
    return out;
  }, [analysis.subjects, members, selectedMember]);

  const targetFor = useCallback(
    (subjectKey) => {
      if (subjectKey === 'self') return 'self';
      if (subjectKey in targets) return targets[subjectKey] || null;
      const pre = matches[subjectKey]?.preMatch;
      return pre ? memberKey(pre) : null;
    },
    [targets, matches]
  );

  const isSelected = useCallback(
    (subjectKey, f) => {
      if (f.alreadyRecorded) return false;
      const k = findingKey(subjectKey, f);
      if (k in ticks) return ticks[k];
      if (
        subjectKey !== 'self' &&
        !(targets[subjectKey] ?? matches[subjectKey]?.preMatch)
      )
        return false;
      return !!f.preselect;
    },
    [ticks, targets, matches]
  );

  const setTarget = (subjectKey, key) =>
    setTargets((prev) => ({ ...prev, [subjectKey]: key || '' }));

  const toggleFinding = (subjectKey, f) =>
    setTicks((prev) => ({
      ...prev,
      [findingKey(subjectKey, f)]: !isSelected(subjectKey, f)
    }));

  const selectedItems = useMemo(() => {
    const out = [];
    for (const s of analysis.subjects) {
      const t = targetFor(s.key);
      if (!t) continue;
      for (const f of s.findings)
        if (isSelected(s.key, f))
          out.push({ subject: s, finding: f, target: t });
    }
    return out;
  }, [analysis.subjects, targetFor, isSelected]);

  // `sourceName` names the uploaded file a 'pdf-upload' text came from.
  const analyzeText = async (text = '', source = 'blur', sourceName = null) => {
    const trimmed = (text || '').trim();
    if (!trimmed) {
      if (source !== 'pdf-upload') setNoteAnalysis(null);
      return;
    }
    setAnalysesInFlight((n) => n + 1);
    try {
      const res = await axios.post('/clinical-notes/analyze-note', {
        noteText: trimmed,
        userId: user_id,
        familyMemberId,
        isProband: !!isProband
      });
      if (res.data.success) {
        // Each finding remembers where its text came from, so the record it
        // becomes is labelled truthfully (TGCLOUD-1866 D8).
        const sourceKind = source === 'pdf-upload' ? 'upload' : 'typed';
        const incoming = {
          ...res.data,
          subjects: subjectsOf(res.data).map((s) => ({
            ...s,
            findings: (s.findings || []).map((x) => ({ ...x, sourceKind }))
          }))
        };
        // Only an answer carrying HPO-derived suggestions says anything about
        // the release; a genes-only answer leaves the one already shown, which
        // still names the source of the phenotypes and diseases on screen.
        const hpoDerived = incoming.subjects.some((s) =>
          s.findings.some((f) => f.kind === 'phenotype' || f.kind === 'disease')
        );
        if (hpoDerived) setHpoRelease(res.data.hpoRelease || null);
        // `sourceKind` says where the text came from: an uploaded file, or
        // text typed into the form (TGCLOUD-1866).
        if (source === 'pdf-upload') {
          if (!forgottenSources.current.has(sourceName)) {
            setAttachmentAnalyses((prev) => [
              ...prev,
              { ...incoming, sourceName, sourceKind }
            ]);
          }
        } else setNoteAnalysis({ ...incoming, sourceKind });
      }
    } catch (err) {
      console.warn('Analyze-note request failed:', err?.message || err);
    } finally {
      setAnalysesInFlight((n) => Math.max(0, n - 1));
    }
  };

  const handleTextBlur = (e) =>
    analyzeText(e?.target?.value || '', 'text-blur');
  // Once per uploaded file, with that file's own text and name.
  const handleAttachmentText = (text, sourceName = null) =>
    analyzeText(text, 'pdf-upload', sourceName);
  // Stop offering what was read from one uploaded file (by the name it was analysed under).
  const forgetAttachment = useCallback((sourceName) => {
    if (!sourceName) return;
    forgottenSources.current.add(sourceName);
    setAttachmentAnalyses((prev) =>
      prev.filter((a) => a.sourceName !== sourceName)
    );
  }, []);

  const acceptSuggestions = async ({ clinicalNoteDate, clinician } = {}) => {
    if (!selectedItems.length) return { success: false };
    const memberByKey = new Map(members.map((m) => [memberKey(m), m]));
    const items = [];
    for (const { subject, finding: f, target } of selectedItems) {
      const address =
        target === 'self'
          ? {
              targetFamilyMemberId: familyMemberId,
              targetIsProband: !!isProband
            }
          : memberByKey.get(target)
          ? targetOf(memberByKey.get(target))
          : null;
      if (!address) continue;
      items.push({
        kind: f.kind,
        id: f.id,
        name: f.name,
        status: f.status,
        // Why it is 'uncertain': stated both as a diagnosis and as carrier
        // status (TGCLOUD-1866, the review of D2). The record's note says so.
        conflict: f.conflict || null,
        age: f.age ?? null,
        evidence: (f.evidence || [])[0] || null,
        classification: f.classification || null,
        symbol: f.symbol || null,
        variant: f.variant || null,
        secondHand: !!f.secondHand,
        notSighted: !!f.notSighted,
        sourceKind: f.sourceKind || null,
        subject: subject.key,
        ...address
      });
    }
    if (!items.length) return { success: false };
    const scope = prenatalScopeToSend(sourceScope);
    setSavingNote(true);
    try {
      const res = await axios.post('/clinical-notes/accept-suggestions', {
        userId: user_id,
        familyMemberId,
        familyId: user.familyId,
        isProband: !!isProband,
        // A calendar date, not an instant: a local-midnight Date sent as ISO
        // is the previous day in UTC at any positive offset.
        clinicalNoteDate: clinicalNoteDate
          ? toStoredDate(clinicalNoteDate)
          : undefined,
        clinician,
        items,
        ...(sourceTestId ? { sourceTestId } : {}),
        ...(scope ? { sourceScope: scope } : {})
      });
      if (res.data.success) {
        // Strip stale aggregates so the reducer recomputes them from updated member data
        /* eslint-disable no-unused-vars */
        const {
          diagnoses: _d,
          phenotypes: _p,
          genotypes: _g,
          ...usersBase
        } = users;
        /* eslint-enable no-unused-vars */
        const memberUpdate = {
          phenotypes: res.data.phenotypes || [],
          genotypes: res.data.genotypes || [],
          diagnoses: res.data.diagnoses || []
        };
        dispatchUpdate({
          type: EVENTS.SET_PEDIGREE_SELECTED_USER,
          value: isProband
            ? {
                ...usersBase,
                selectedUser: { ...users.selectedUser, ...memberUpdate }
              }
            : {
                ...usersBase,
                family: (users.family || []).map((m) =>
                  m.familyMemberId === familyMemberId
                    ? { ...m, ...memberUpdate }
                    : m
                )
              }
        });
        onAccepted?.({
          phenotypes: res.data.phenotypes,
          genotypes: res.data.genotypes,
          diagnoses: res.data.diagnoses
        });
        // Relatives' records changed too: re-read the family so the chart and
        // their sidebars show what was added.
        if (items.some((i) => i.subject !== 'self'))
          await family.refreshFamily?.();
        const failed = (res.data.results || []).filter((r) => r.error);
        // No genotype was written from a record about a pregnancy (TGCLOUD-1868, decision 17):
        // said only where one was asked for and skipped for it (each in its person's `skipped`).
        const genotypeSkipped =
          res.data.genotypeSkipped === 'prenatal' &&
          (res.data.results || []).some((r) =>
            (r.skipped || []).some((s) => s.reason === 'prenatal-source')
          )
            ? 'prenatal'
            : null;
        setLastAccept({ failed, count: res.data.count, genotypeSkipped });
        setAttachmentAnalyses([]);
        setNoteAnalysis(null);
        setTicks({});
        setTargets({});
        return {
          success: true,
          count: res.data.count,
          failed,
          genotypeSkipped
        };
      }
    } catch {
      // caller shows the error
    } finally {
      setSavingNote(false);
    }
    return { success: false };
  };

  // The pre-1845 surface, still read by the forms.
  const suggestions = useMemo(
    () =>
      analysis.subjects.flatMap((s) =>
        s.findings.map((f) => ({ ...f, subject: s.key }))
      ),
    [analysis.subjects]
  );
  const selectedSuggestions = selectedItems.map((x) => x.finding);

  return {
    // per person (TGCLOUD-1845)
    analysis,
    matches,
    members,
    targetFor,
    setTarget,
    isSelected,
    toggleFinding,
    selectedCount: selectedItems.length,
    lastAccept,
    // shared
    suggestions,
    analyzingNote,
    selectedSuggestions,
    savingNote,
    handleTextBlur,
    handleAttachmentText,
    forgetAttachment,
    acceptSuggestions,
    analyzeText,
    hpoRelease
  };
}
